import { useDispatch } from "react-redux";

import { ViewList } from "@material-ui/icons";
import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import { useChartsContext } from "contexts/ChartContext";
import { useAtomValue } from "jotai";
import { updateChartByPath } from "store/features";
import styled from "styled-components/macro";

import { useChartEntities } from "hooks/charts";

import { ToolbarButton } from "components/base";
import {
  FullscreenToggle,
  LegendToggle,
  ScreenshotToggle
} from "components/charts/toggles";

import { visibilityToggleStyles } from "../../shared/SharedStyles";
import RigFracChartSelector from "./RigFracChartSelector";
import RigFracColorPaletteSelector from "./RigFracColorPaletteSelector";
import RigFracGroupBySelector from "./RigFracGroupBySelector";

/**
 * Minimal toolbar for the Rig/Frac Activity chart (EVA-76).
 *
 * NOT the shared ChartToolbar: its Well component array hard-renders the wells
 * ChartSelector + ProductSelector header (ChartHeader) and toggles that don't
 * read feature `show` flags, so it would surface wells-only controls on this
 * chart. The scoped RigFracChartSelector renders RIG_FRAC_CHART_TYPES only.
 *
 * Follows the standard chart header layout: a data-table toggle on the left of
 * the chart selector (export lives inside the data table, not on the toolbar).
 */
function RigFracChartToolbar() {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { screenshot, dataTable } = useChartEntities(id);
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;
  const isScreenshotActive = screenshot?.active ?? false;
  const isDataTableActive = dataTable?.active ?? false;
  const isDataTableEnabled = dataTable?.enabled ?? false;

  const toggleDataTable = (visible: boolean) => {
    dispatch(
      updateChartByPath({ id, path: `features.datatable.active`, value: visible })
    );
  };

  // Fully hidden during capture (not just emptied) — the shared toolbar keeps an
  // empty bar visible on screenshot, which showed up as a white band at the top
  // of the rig/frac capture. The chart doesn't need any toolbar in the shot.
  if (isScreenshotActive) return null;

  return (
    <Wrapper
      className={classnames({ visible: isHover })}
      data-testid="rig-frac-chart-toolbar">
      <HeaderGroup>
        {isDataTableEnabled && (
          <ToolbarButton
            style={{ marginLeft: "4px", marginRight: "4px" }}
            active={isDataTableActive}
            icon={<ViewList fontSize="large" />}
            tooltipText="Data Table"
            overflowLabel="Data Table"
            onToggle={toggleDataTable}
            data-testid="rig-frac-data-table-toggle"
          />
        )}
        <Divider />
        <RigFracChartSelector />
      </HeaderGroup>
      <ToolbarContent>
        <RigFracGroupBySelector />
        <RigFracColorPaletteSelector />
        <LegendToggle />
        <ScreenshotToggle />
        <FullscreenToggle />
      </ToolbarContent>
    </Wrapper>
  );
}

export default RigFracChartToolbar;

const Wrapper = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--chart-toolbar-height);
  display: flex;
  align-items: center;
  background-color: var(--white);
  box-shadow: 0 14px 16px 0 rgb(45 53 63 / 10%), inset 0 7px 8px -10px rgb(45 53 63 / 15%);
  z-index: 50;
  padding-right: 4px;
`;

const HeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Divider = styled.div`
  width: 1px;
  height: var(--chart-toolbar-height);
  background-color: #d9e1e2;
`;

const ToolbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  padding-right: 4px;
`;
